// The live ride page: what to say for each status, where the chauffeur is on the map, how often to ask the server. The server decides everything that matters
// (the status, the timeline, the position, the ETA); these only turn it into words and drawing coordinates.
import { str, num } from './safe';

export const OPEN_STATUSES = ['pending', 'assigned', 'en_route_to_pickup', 'arrived', 'on_trip'];
export const isOpen = (status) => OPEN_STATUSES.includes(str(status));

// The headline and the line under it for the current moment.
export function headline(ride, tracking) {
  const status = str(ride && ride.status) || 'pending';
  const name = str(ride && ride.driverName) || (ride && ride.driver && typeof ride.driver === 'object' ? str(ride.driver.name) : '');
  const eta = tracking && !tracking.stale && Number.isFinite(Number(tracking.etaMinutes)) ? Math.max(0, Math.round(Number(tracking.etaMinutes))) : null;
  if (ride && ride.awaitingPayment) return { title: 'Complete your payment', sub: 'Your booking is held for a few minutes until it is paid.' };
  switch (status) {
    case 'completed': return { title: 'Ride complete', sub: 'Thank you for riding with RideBuraq.' };
    case 'cancelled': return { title: 'Ride cancelled', sub: 'This ride was cancelled.' };
    case 'on_trip': return { title: 'Enjoy your ride', sub: eta != null ? `About ${eta} min to your destination.` : 'Your chauffeur is taking you to your destination.' };
    case 'arrived': return { title: 'Your chauffeur has arrived', sub: name ? `${name} is waiting for you.` : 'Your chauffeur is waiting for you.' };
    case 'en_route_to_pickup': return { title: eta != null ? `Your chauffeur is ${eta} min away` : 'Your chauffeur is on the way', sub: name ? `${name} is heading to your pickup.` : 'Heading to your pickup.' };
    case 'assigned': return { title: name ? `${name} will be your chauffeur` : 'Your chauffeur is confirmed', sub: 'We will show you the car and its position as soon as it is on the way.' };
    default: return { title: 'Handpicking your chauffeur', sub: 'You will be notified as soon as your chauffeur is confirmed.' };
  }
}

// How long to wait before asking again (milliseconds): quick while the chauffeur is moving, slow while waiting, none once the ride is over.
export function pollDelay(status, tracking) {
  const s = str(status);
  if (!isOpen(s)) return 0;
  if (tracking && tracking.live) return 4000;
  if (s === 'pending') return 15000;
  return 8000;
}

// Project points onto a width x height drawing (a flat projection that keeps shapes right at this latitude), padded; returns { project(pt) -> {x, y}, ok }.
export function makeProjector(points, width, height, pad = 28) {
  const pts = (points || []).filter((p) => p && Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)));
  if (!pts.length) return { ok: false, project: () => ({ x: width / 2, y: height / 2 }) };
  const lat0 = pts.reduce((a, p) => a + Number(p.lat), 0) / pts.length;
  const k = Math.cos((lat0 * Math.PI) / 180);
  const xs = pts.map((p) => Number(p.lng) * k); const ys = pts.map((p) => Number(p.lat));
  const minX = Math.min(...xs); const maxX = Math.max(...xs); const minY = Math.min(...ys); const maxY = Math.max(...ys);
  const spanX = Math.max(maxX - minX, 0.002); const spanY = Math.max(maxY - minY, 0.002);       // a minimum span so one point does not zoom to nothing
  const scale = Math.min((width - 2 * pad) / spanX, (height - 2 * pad) / spanY);
  const offX = (width - spanX * scale) / 2; const offY = (height - spanY * scale) / 2;
  return { ok: true, project: (p) => ({ x: offX + (Number(p.lng) * k - minX) * scale, y: height - (offY + (Number(p.lat) - minY) * scale) }) };
}

export const pathFrom = (points, project) => (points || []).filter((p) => p && Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)))
  .map((p, i) => { const { x, y } = project(p); return `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`; }).join('');

export async function fetchLive(api, token, reference, signal) {
  const r = await fetch(`${api}/api/rides/${encodeURIComponent(reference)}/live`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, signal });
  if (r.status === 404) return { notFound: true };
  if (!r.ok) throw new Error('We could not refresh your ride. Retrying…');
  return r.json();
}

export async function startCall(api, token, reference) {
  const r = await fetch(`${api}/api/rides/${encodeURIComponent(reference)}/masked-call`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: '{}' });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) { const d = j && j.detail; throw new Error((d && typeof d === 'object' && d.message) || (typeof d === 'string' && d) || 'We could not start the call. Please try again.'); }
  return j;
}

// "Oct 20, 4:30 PM" in New York time from an ISO string; '' when it is not a date.
export function nyTime(iso) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return '';
  try { return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' })}, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })}`; } catch (e) { return ''; }
}
export const livePosition = (tracking) => (tracking && Number.isFinite(num(tracking.lat, NaN)) && Number.isFinite(num(tracking.lng, NaN)) ? { lat: num(tracking.lat), lng: num(tracking.lng) } : null);
